import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { AzureAD } from 'react-aad-msal'
import { authProvider } from '../../../services/authProvider'
import store from '../../../store/configureStore'
import { Container, Row, Col, Card, CardBody, Badge } from 'reactstrap'
import PageContainer from '../../Shared/LayoutComponents/PageContainer'
import { parseIso } from '../../Shared/formatDateTime'
import PageHeading from '../../Shared/LayoutComponents/PageHeading'
import LoadingSpinner from '../../Shared/LoadingSpinner'
import CSPRenewalsTable from './CSPRenewalsTable'
import NotificationHistoryModal from './NotificationHistoryModal'
import SettingsModal from './SettingsModal'
import {
  fetchDashboard,
  fetchTenants,
  fetchUnmappedTenants,
  fetchNotifications,
  triggerSync,
} from '../../actions/cspRenewalsActions'

const CSPRenewalsInner = ({ isAdmin }) => {
  const dispatch = useDispatch()
  const [historyModalOpen, setHistoryModalOpen] = useState(false)
  const [settingsModalOpen, setSettingsModalOpen] = useState(false)
  useEffect(() => {
    dispatch(fetchDashboard())
    dispatch(fetchTenants())
    dispatch(fetchUnmappedTenants())
    dispatch(fetchNotifications())
  }, [dispatch, isAdmin])

  const { loading, dashboard, syncTriggering, unmappedTenants } = useSelector((state) => state.cspRenewals)

  const formattedLastSync = React.useMemo(() => {
    if (!dashboard?.lastSyncDate) return null
    // parseIso, not `new Date(...)`: the API emits offset-less timestamps, and
    // a bare parse reads them as local time - which silently displayed the
    // right number only while the sync stored AWST wall-clock in a UTC field.
    return parseIso(dashboard.lastSyncDate)?.toLocaleString('en-AU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
  }, [dashboard])

  const unmappedCount = (unmappedTenants || []).length

  return (
    <PageContainer>
      <Container fluid className="mt-3 mb-4">
        <Row className="align-items-center mb-4">
          <Col>
            <PageHeading>CSP Renewals</PageHeading>
            {formattedLastSync && (
              <small className="text-muted">Data last synced on {formattedLastSync}</small>
            )}
          </Col>
          <Col xs="auto" className="d-flex align-items-center">
            <Link
              to="/CSPRenewals/Tenants"
              className="btn btn-outline-secondary mr-2"
            >
              Tenant Settings
              {unmappedCount > 0 && (
                <Badge color="primary" className="ml-1">{unmappedCount}</Badge>
              )}
            </Link>
            <Link
              to="/CSPRenewals/Products"
              className="btn btn-outline-secondary mr-2"
            >
              Products
            </Link>
            <Link
              to="/CSPRenewals/Queue"
              className="btn btn-outline-secondary mr-2"
            >
              Queue
            </Link>
            <button
              className="btn btn-outline-secondary mr-2"
              onClick={() => setHistoryModalOpen(true)}
            >
              Notification History
            </button>
            <button
              className="btn btn-outline-secondary mr-2"
              onClick={() => setSettingsModalOpen(true)}
            >
              Settings
            </button>
            {isAdmin && (
              <button
                className="btn btn-primary"
                disabled={syncTriggering || loading}
                onClick={() => dispatch(triggerSync())}
              >
                {syncTriggering ? (
                  <>
                    <span className="spinner-border spinner-border-sm mr-1" role="status" />
                    Queueing...
                  </>
                ) : (
                  'Sync from Dicker Data'
                )}
              </button>
            )}
          </Col>
        </Row>

        {/* Summary cards */}
        <Row className="mb-4">
          <Col md={2}>
            <Card>
              <CardBody className="text-center">
                <h4 className="mb-0">{dashboard?.totalSubscriptions ?? 0}</h4>
                <small className="text-muted">Subscriptions</small>
              </CardBody>
            </Card>
          </Col>
          <Col md={2}>
            <Card className="border-success">
              <CardBody className="text-center">
                <h4 className="mb-0 text-success">{dashboard?.notificationsSentThisMonth ?? 0}</h4>
                <small className="text-muted">Notifications this month</small>
              </CardBody>
            </Card>
          </Col>
          <Col md={2}>
            {/*
              When there are unmapped active customers, this card becomes the
              page's primary call-to-action. Click anywhere on the card to land
              on the Tenants page pre-filtered to the Unmapped view, where
              sales admin can map them inline. When the count is zero, the
              card stays in its dimmed neutral state.
            */}
            {unmappedCount > 0 ? (
              <Link
                to="/CSPRenewals/Tenants?view=unmapped"
                className="text-decoration-none"
                title={`${unmappedCount} active tenant${unmappedCount === 1 ? '' : 's'} unmapped. Click to action.`}
              >
                <Card className="border-warning" style={{ cursor: 'pointer' }}>
                  <CardBody className="text-center">
                    <h4 className="mb-0 text-warning">{unmappedCount}</h4>
                    <small className="text-muted">Unmapped</small>
                  </CardBody>
                </Card>
              </Link>
            ) : (
              <Card style={{ borderColor: '#adb5bd' }}>
                <CardBody className="text-center">
                  <h4 className="mb-0" style={{ color: '#adb5bd' }}>0</h4>
                  <small className="text-muted">Unmapped</small>
                </CardBody>
              </Card>
            )}
          </Col>
          <Col md={2}>
            {(() => {
              const noRecipients = dashboard?.mappedTenantsWithNoRecipients ?? 0
              return noRecipients > 0 ? (
                <Card className="border-danger">
                  <CardBody className="text-center">
                    <h4 className="mb-0 text-danger">{noRecipients}</h4>
                    <small className="text-muted">No recipients</small>
                  </CardBody>
                </Card>
              ) : (
                <Card style={{ borderColor: '#adb5bd' }}>
                  <CardBody className="text-center">
                    <h4 className="mb-0" style={{ color: '#adb5bd' }}>0</h4>
                    <small className="text-muted">No recipients</small>
                  </CardBody>
                </Card>
              )
            })()}
          </Col>
        </Row>

        {loading ? (
          <LoadingSpinner />
        ) : (
          <CSPRenewalsTable rows={dashboard?.subscriptions || []} isAdmin={isAdmin} />
        )}
      </Container>

      <NotificationHistoryModal
        isOpen={historyModalOpen}
        toggle={() => setHistoryModalOpen(false)}
      />
      <SettingsModal
        isOpen={settingsModalOpen}
        toggle={() => setSettingsModalOpen(false)}
        isAdmin={isAdmin}
      />
    </PageContainer>
  )
}

const CSPRenewals = () => {
  return (
    <AzureAD provider={authProvider} reduxStore={store}>
      {({ accountInfo }) => {
        const roles = accountInfo?.account?.idToken?.roles || []
        const isAdmin = roles.includes('Admin')
        const isSales = roles.includes('Sales')

        if (!isAdmin && !isSales) {
          return (
            <PageContainer>
              <Container fluid className="mt-3">
                <p>You do not have permission to view this page.</p>
              </Container>
            </PageContainer>
          )
        }

        // Sales has full edit rights inside the CSP renewals dashboard - they
        // own the workflow (mappings, recipients, products, settings). Pass
        // canEdit as the existing isAdmin prop so every nested gate honours it.
        return <CSPRenewalsInner isAdmin={isAdmin || isSales} />
      }}
    </AzureAD>
  )
}

export default CSPRenewals
